'use client';

import { useCallback, useState } from 'react';
import type {
  VideoRecommendationItem,
  VideoRecommendationsWidgetResolved,
} from './types';
import { VideoCard, VideoCardMobile } from './VideoCard';
import { Carousel } from '@/components/Carousel';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { Text } from '@/lib/render/Text';

const DESKTOP_PER_PAGE = 3;

function useVideoPlayback(
  tracking: VideoRecommendationsWidgetResolved['tracking'],
) {
  const [openedVideos, setOpenedVideos] = useState<ReadonlySet<number>>(
    new Set(),
  );
  const [activeVideoId, setActiveVideoId] = useState<number | null>(null);

  const handlePlay = useCallback(
    (id: number, title: string | undefined) => {
      // `video_name` is always the tapped video's own title and `{label}` is
      // the only placeholder the widget's tracking config offers (spec
      // 10.03.02.01.11 § GA4 tracking). With no title there is nothing to
      // attribute the event to, so it is not sent at all — the Strapi field
      // hint tells editors that untitled videos are untracked (SH-445).
      if (title) {
        pushCmsEventTracking(tracking, { label: title });
      }
      setOpenedVideos((prev) => {
        if (prev.has(id)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setActiveVideoId(id);
    },
    [tracking],
  );

  const handlePageChange = useCallback(() => {
    setOpenedVideos(new Set());
    setActiveVideoId(null);
  }, []);

  return { openedVideos, activeVideoId, handlePlay, handlePageChange };
}

function DesktopVideos({
  videos,
  tracking,
}: {
  videos: VideoRecommendationItem[];
  tracking: VideoRecommendationsWidgetResolved['tracking'];
}) {
  const { openedVideos, activeVideoId, handlePlay, handlePageChange } =
    useVideoPlayback(tracking);
  const needsCarousel = videos.length > DESKTOP_PER_PAGE;

  return needsCarousel ? (
    <Carousel
      slidesPerView={DESKTOP_PER_PAGE}
      showDots
      prevLabel="Vorheriges Video"
      nextLabel="Nächstes Video"
      // A dot is a *page* of up to `DESKTOP_PER_PAGE` videos, and `Carousel`
      // indexes `dotLabels` by page. Labelling them with video titles named a
      // video that was not on the page the dot opened, and broke outright once
      // the title became optional (SH-445).
      dotLabels={Array.from(
        { length: Math.ceil(videos.length / DESKTOP_PER_PAGE) },
        (_, i) => `Gehe zu Seite ${i + 1}`,
      )}
      onPageChange={handlePageChange}
      slides={videos.map((video, i) => (
        <VideoCard
          key={video.id}
          video={video}
          isOpen={openedVideos.has(video.id)}
          isPlaying={activeVideoId === video.id}
          onPlay={() => handlePlay(video.id, video.title)}
          fallbackLabel={`Video ${i + 1}`}
        />
      ))}
    />
  ) : (
    <div className="flex justify-center gap-0">
      {videos.map((video, i) => (
        // Share the row between however many videos there are, capped at
        // `DESKTOP_PER_PAGE`. Dividing by the cap unconditionally left a
        // single video at a third of the width, marooned in the middle.
        <div
          key={video.id}
          style={{
            width: `${100 / Math.min(DESKTOP_PER_PAGE, videos.length)}%`,
          }}
        >
          <VideoCard
            video={video}
            isOpen={openedVideos.has(video.id)}
            isPlaying={activeVideoId === video.id}
            onPlay={() => handlePlay(video.id, video.title)}
            fallbackLabel={`Video ${i + 1}`}
            // A lone video owns the whole row, but the card's own 277px cap
            // kept it small in the middle of it. Only this case lifts the cap
            // (SH-445); two and three videos keep it.
            enlarged={videos.length === 1}
          />
        </div>
      ))}
    </div>
  );
}

function MobileVideos({
  videos,
  tracking,
}: {
  videos: VideoRecommendationItem[];
  tracking: VideoRecommendationsWidgetResolved['tracking'];
}) {
  const { openedVideos, activeVideoId, handlePlay, handlePageChange } =
    useVideoPlayback(tracking);

  return (
    <Carousel
      className="m-0"
      slideClassName=""
      arrowsOnBottom
      prevLabel="Vorheriges Video"
      nextLabel="Nächstes Video"
      // One video per page here, so the index is exact — but still positional
      // rather than title-based, for the same reason as the desktop dots.
      dotLabels={videos.map((_, i) => `Gehe zu Video ${i + 1}`)}
      // A lone video has nowhere to navigate to (SH-445). Its caption still
      // renders: `Carousel` draws that independently of the controls.
      showArrows={videos.length > 1}
      showDots={videos.length > 1}
      onPageChange={handlePageChange}
      slides={videos.map((video, i) => (
        <VideoCardMobile
          key={video.id}
          video={video}
          isOpen={openedVideos.has(video.id)}
          isPlaying={activeVideoId === video.id}
          onPlay={() => handlePlay(video.id, video.title)}
          fallbackLabel={`Video ${i + 1}`}
        />
      ))}
      captions={videos.map((video) => (
        <span key={video.id}>
          {video.title && <Text className="font-medium">{video.title}</Text>}
          {video.description && (
            <Text className="block">{video.description}</Text>
          )}
        </span>
      ))}
    />
  );
}

interface VideoRecommendationsComponentProps {
  resolved: VideoRecommendationsWidgetResolved;
}

export const VideoRecommendationsComponent = ({
  resolved,
}: VideoRecommendationsComponentProps) => {
  if (!resolved.videos || resolved.videos.length === 0) {
    return null;
  }

  const { videos } = resolved;

  return (
    // A real element rather than a Fragment: the widget needs one root to carry
    // the `pt-4` every widget uses for inter-widget rhythm (see CLAUDE.md).
    <div className="pt-4">
      <div className="hidden md:block">
        <DesktopVideos videos={videos} tracking={resolved.tracking} />
      </div>
      <div className="md:hidden">
        <MobileVideos videos={videos} tracking={resolved.tracking} />
      </div>
    </div>
  );
};
